import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { supabase } from '../../lib/supabase';

export interface NotificationRow {
  id: string;
  organization_id: string | null;
  user_id: string;
  kind: string;
  title: string;
  body: string | null;
  entity_type: string | null;
  entity_id: string | null;
  read_at: string | null;
  created_at: string;
}

const NOTIFICATIONS_KEY = (userId: string | null) => ['notifications', userId] as const;

async function fetchNotifications(userId: string): Promise<NotificationRow[]> {
  const { data, error } = await supabase
    .from('notifications')
    .select('id, organization_id, user_id, kind, title, body, entity_type, entity_id, read_at, created_at')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(20);

  if (error) throw error;
  return data;
}

export function useNotifications(userId: string | null) {
  return useQuery({
    queryKey: NOTIFICATIONS_KEY(userId),
    queryFn: () => {
      if (!userId) return Promise.resolve<NotificationRow[]>([]);
      return fetchNotifications(userId);
    },
    enabled: Boolean(userId),
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
  });
}

export function useMarkNotificationRead(userId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY(userId) });
    },
  });
}

export function useMarkAllNotificationsRead(userId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error('Missing user context');
      const { error } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('user_id', userId)
        .is('read_at', null);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY(userId) });
    },
  });
}
